import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPlus,
  FaTrash,
  FaGraduationCap,
  FaPencilRuler,
  FaBus,
  FaCalendarAlt,
  FaExclamationTriangle,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const RECEIPT_TYPES = [
  { v: 'term', l: 'Term / Tuition', Icon: FaGraduationCap, accent: '#1d4ed8' },
  { v: 'exam', l: 'Exam Fees', Icon: FaPencilRuler, accent: '#7c3aed' },
  { v: 'transport', l: 'Transport / Hostel', Icon: FaBus, accent: '#0e7490' },
  { v: 'annual', l: 'Annual / Admission', Icon: FaCalendarAlt, accent: '#15803d' },
  { v: 'fine', l: 'Fine / Late Fee', Icon: FaExclamationTriangle, accent: '#b91c1c' },
];

const TEMPLATES = [
  { id: 'classic', label: 'School Classic A4', accent: '#1d4ed8', format: 'a4' },
  { id: 'modern', label: 'Modern Sidebar', accent: '#7c3aed', format: 'a4' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: 'a4' },
  { id: 'banner', label: 'Bold Banner', accent: '#0e7490', format: 'a4' },
  { id: 'thermal', label: 'Quick Slip 80mm', accent: '#15803d', format: [80, 297] },
];

const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Net Banking', 'Cheque', 'DD', 'Wallet', 'Bank Transfer'];

const PREFIX_MAP = { term: 'FEE', exam: 'EXM', transport: 'TRN', annual: 'ADM', fine: 'FIN' };

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDMY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const todayDMY = () => toDMY(new Date());

const academicYearString = () => {
  const d = new Date();
  const y = d.getFullYear();
  // Indian academic year runs Apr–Mar
  if (d.getMonth() >= 3) return `${y}-${String(y + 1).slice(-2)}`;
  return `${y - 1}-${String(y).slice(-2)}`;
};

const genReceiptNo = (prefix) => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const numToWordsIndian = (num) => {
  num = Math.round(Number(num) || 0);
  if (num === 0) return 'Zero';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
  };
  let str = '';
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const hundred = num;
  if (crore) str += inWords(crore) + ' Crore ';
  if (lakh) str += inWords(lakh) + ' Lakh ';
  if (thousand) str += inWords(thousand) + ' Thousand ';
  if (hundred) str += inWords(hundred);
  return str.trim();
};

const newItem = (head = '', amount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  head,
  amount,
});

const DEFAULT_ITEMS_BY_TYPE = {
  term: [
    newItem('Tuition Fee', 18000),
    newItem('Computer Lab Fee', 1500),
    newItem('Library Fee', 800),
    newItem('Sports Fee', 700),
  ],
  exam: [
    newItem('Term Exam Fee', 1200),
    newItem('Practical Exam Fee', 500),
    newItem('Marksheet / Certificate Fee', 200),
  ],
  transport: [
    newItem('Transport Fee (Route 4)', 4500),
    newItem('Hostel Fee (Monthly)', 6500),
    newItem('Mess Charges', 2500),
  ],
  annual: [
    newItem('Admission Fee', 5000),
    newItem('Annual Charges', 6000),
    newItem('Uniform & Books', 3500),
    newItem('Caution / Security Deposit', 2000),
  ],
  fine: [
    newItem('Late Fee Fine', 200),
    newItem('Damage / Misc. Penalty', 0),
  ],
};

const SchoolFeesReceiptGenerator = () => {
  const [receiptType, setReceiptType] = useState('term');
  const [template, setTemplate] = useState('classic');

  const [school, setSchool] = useState({
    name: 'Sunrise Public School',
    address: 'Plot 14, Sector 21, Noida 201301, Uttar Pradesh',
    phone: '+91 120 4567 890',
    email: 'office@sunrisepublic.edu.in',
    code: 'CBSE-AFF-2330456',
    branch: 'Main Campus',
  });

  const [student, setStudent] = useState({
    name: 'Aanya Verma',
    admissionNo: 'ADM-2024-1107',
    classGrade: 'Class VIII',
    section: 'B',
    rollNo: '17',
    fatherName: 'Mr. Rajeev Verma',
    motherName: 'Mrs. Priya Verma',
    phone: '+91 98100 22334',
    email: 'rajeev.verma@example.com',
  });

  const [meta, setMeta] = useState({
    receiptNo: genReceiptNo('FEE'),
    date: todayDMY(),
    academicYear: academicYearString(),
    term: 'Term I (Apr - Sep)',
    paymentMode: 'UPI',
    txnRef: 'UPI/QR-993421',
    chequeNo: '',
    bankName: '',
    cashier: 'Mrs. Sunita Mehra',
  });

  const [items, setItems] = useState(DEFAULT_ITEMS_BY_TYPE.term);

  const [charges, setCharges] = useState({
    previousBalance: 0,
    lateFine: 0,
    discount: 0,
    scholarship: 0,
    roundOff: true,
    amountPaid: 0, // 0 = full payment; otherwise partial
  });

  const [notes, setNotes] = useState(
    'Fees once paid are non-refundable and non-transferable. Please retain this receipt for future reference.'
  );

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#1d4ed8';
  const typeConfig = RECEIPT_TYPES.find((t) => t.v === receiptType);

  const switchType = (type) => {
    setReceiptType(type);
    setMeta((m) => ({ ...m, receiptNo: genReceiptNo(PREFIX_MAP[type] || 'FEE') }));
    setItems(DEFAULT_ITEMS_BY_TYPE[type] || [newItem()]);
    if (type === 'fine') {
      setCharges((c) => ({ ...c, lateFine: 200, discount: 0, scholarship: 0 }));
    }
  };

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    const itemsSum = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const prev = Number(charges.previousBalance) || 0;
    const fine = Number(charges.lateFine) || 0;
    const disc = Number(charges.discount) || 0;
    const sch = Number(charges.scholarship) || 0;
    const gross = itemsSum + prev + fine;
    const afterDisc = Math.max(0, gross - disc - sch);
    const grandRounded = Math.round(afterDisc);
    const paid = Number(charges.amountPaid) > 0 ? Number(charges.amountPaid) : (charges.roundOff ? grandRounded : afterDisc);
    const balanceDue = Math.max(0, (charges.roundOff ? grandRounded : afterDisc) - paid);
    return {
      itemsSum,
      prev,
      fine,
      disc,
      sch,
      gross,
      grand: Math.round(afterDisc * 100) / 100,
      grandRounded,
      roundOff: grandRounded - afterDisc,
      paid,
      balanceDue,
    };
  }, [items, charges]);

  const finalAmount = charges.roundOff ? totals.grandRounded : totals.grand;
  const amountInWords = numToWordsIndian(totals.paid > 0 ? totals.paid : finalAmount);

  const updateItem = (id, patch) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const handleReset = () => {
    setSchool({ name: '', address: '', phone: '', email: '', code: '', branch: '' });
    setStudent({
      name: '', admissionNo: '', classGrade: '', section: '', rollNo: '',
      fatherName: '', motherName: '', phone: '', email: '',
    });
    setMeta({
      receiptNo: genReceiptNo(PREFIX_MAP[receiptType] || 'FEE'),
      date: todayDMY(),
      academicYear: academicYearString(),
      term: '',
      paymentMode: 'Cash',
      txnRef: '',
      chequeNo: '',
      bankName: '',
      cashier: '',
    });
    setItems([newItem()]);
    setCharges({ previousBalance: 0, lateFine: 0, discount: 0, scholarship: 0, roundOff: true, amountPaid: 0 });
    setNotes('');
  };

  const typeLabel = typeConfig?.l || 'School Fees Receipt';
  const headerTitle = {
    term: 'TUITION / TERM FEE RECEIPT',
    exam: 'EXAMINATION FEE RECEIPT',
    transport: 'TRANSPORT / HOSTEL FEE RECEIPT',
    annual: 'ADMISSION / ANNUAL FEE RECEIPT',
    fine: 'LATE FEE / FINE RECEIPT',
  }[receiptType] || 'SCHOOL FEE RECEIPT';

  // ---------- PDF HELPERS ----------
  const drawItemsTable = (doc, startY, left, right, color) => {
    let y = startY;
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('#', left + 3, y + 6);
    doc.text('Fee Head / Particulars', left + 12, y + 6);
    doc.text('Amount (Rs.)', right - 3, y + 6, { align: 'right' });
    y += 9;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    items.forEach((it, idx) => {
      const headLines = doc.splitTextToSize(it.head || '-', right - left - 50);
      const rowH = Math.max(8, headLines.length * 4.4 + 2);
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), left + 3, y + 5.5);
      doc.text(headLines, left + 12, y + 5.5);
      doc.text(formatINR(it.amount), right - 3, y + 5.5, { align: 'right' });
      y += rowH;
    });
    return y;
  };

  const drawTotalsBlock = (doc, startY, left, right, color) => {
    let y = startY + 4;
    const totalsX = right - 95;
    const row = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, totalsX, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    row('Fee Items Subtotal', formatINR(totals.itemsSum));
    if (totals.prev > 0) row('Previous Balance', formatINR(totals.prev));
    if (totals.fine > 0) row('Late Fee / Fine', formatINR(totals.fine));
    if (totals.disc > 0) row('Discount', `- ${formatINR(totals.disc)}`);
    if (totals.sch > 0) row('Scholarship / Concession', `- ${formatINR(totals.sch)}`);
    if (charges.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(totalsX, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    row('TOTAL PAYABLE', `Rs. ${formatINR(finalAmount)}`, true, true);
    doc.setTextColor(0, 0, 0);
    row(totals.balanceDue > 0 ? 'Amount Paid' : 'Amount Paid', `Rs. ${formatINR(totals.paid)}`, true);
    if (totals.balanceDue > 0) {
      doc.setTextColor(185, 28, 28);
      row('Balance Due', `Rs. ${formatINR(totals.balanceDue)}`, true);
      doc.setTextColor(0, 0, 0);
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const wordsLines = doc.splitTextToSize(`Amount in words: Rupees ${amountInWords} Only`, right - left);
    wordsLines.forEach((ln) => { doc.text(ln, left, y); y += 4.5; });
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const payDesc = meta.paymentMode === 'Cheque' || meta.paymentMode === 'DD'
      ? `${meta.paymentMode} No: ${meta.chequeNo || '—'}${meta.bankName ? '  ·  ' + meta.bankName : ''}`
      : `Mode: ${meta.paymentMode}${meta.txnRef ? '  ·  Ref: ' + meta.txnRef : ''}`;
    doc.text(payDesc, left, y);
    y += 5;
    return y;
  };

  const drawNotes = (doc, startY, left, right, color) => {
    let y = startY + 4;
    if (!notes) return y;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Notes / Terms', left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    const lines = doc.splitTextToSize(notes, right - left);
    lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    doc.setTextColor(0, 0, 0);
    return y;
  };

  // 1. CLASSIC A4
  const renderClassic = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    let y = 16;
    if (logo) drawPdfLogo(doc, logo, left, 10, 22, school.name, [240, 240, 240]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...color);
    doc.text(school.name || 'School', logo ? left + 26 : left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    if (school.address) {
      const lines = doc.splitTextToSize(school.address, 130);
      lines.forEach((ln) => { doc.text(ln, logo ? left + 26 : left, y); y += 4; });
    }
    const sub = [school.phone, school.email].filter(Boolean).join('  ·  ');
    if (sub) { doc.text(sub, logo ? left + 26 : left, y); y += 4; }
    if (school.code || school.branch) {
      doc.text([school.code && `Affiliation: ${school.code}`, school.branch].filter(Boolean).join('  ·  '), logo ? left + 26 : left, y);
      y += 4;
    }

    y += 2;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(left, y, right, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...color);
    doc.text(headerTitle, left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(60, 60, 60);
    doc.text(`Receipt No: ${meta.receiptNo}`, right, y - 5, { align: 'right' });
    doc.text(`Date: ${meta.date}`, right, y, { align: 'right' });
    y += 8;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Student Details', left, y);
    doc.text('Session Info', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${student.name || '—'}`, left, y);
    doc.text(`Academic Year: ${meta.academicYear || '—'}`, (left + right) / 2, y);
    y += 5;
    doc.text(`Adm. No: ${student.admissionNo || '—'}`, left, y);
    doc.text(`Term: ${meta.term || '—'}`, (left + right) / 2, y);
    y += 5;
    const cls = [student.classGrade, student.section && `Sec. ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  |  ');
    if (cls) doc.text(`Class: ${cls}`, left, y);
    doc.text(`Cashier: ${meta.cashier || '—'}`, (left + right) / 2, y);
    y += 5;
    if (student.fatherName) { doc.text(`Father: ${student.fatherName}`, left, y); y += 5; }
    if (student.motherName) { doc.text(`Mother: ${student.motherName}`, left, y); y += 5; }
    if (student.phone || student.email) {
      doc.text([student.phone, student.email].filter(Boolean).join('  ·  '), left, y);
      y += 5;
    }

    y += 4;
    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);

    // Signatures
    y = Math.min(y + 14, 268);
    doc.setDrawColor(120, 120, 120);
    doc.line(left, y, left + 60, y);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Parent / Student Signature', left + 30, y + 5, { align: 'center' });
    doc.text('Authorised Signatory', right - 30, y + 5, { align: 'center' });

    return doc;
  };

  // 2. MODERN SIDEBAR
  const renderModern = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210, h = 297;
    const sideW = 62;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, sideW, h, 'F');
    if (logo) drawPdfLogo(doc, logo, (sideW - 30) / 2, 14, 30, school.name, [255, 255, 255]);

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const titleY = logo ? 50 : 22;
    const nameLines = doc.splitTextToSize(school.name || 'School', sideW - 10);
    nameLines.forEach((ln, i) => doc.text(ln, sideW / 2, titleY + i * 6, { align: 'center' }));
    let sy = titleY + nameLines.length * 6 + 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (school.address) {
      const lines = doc.splitTextToSize(school.address, sideW - 10);
      lines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    if (school.phone) { doc.text(school.phone, sideW / 2, sy, { align: 'center' }); sy += 3.5; }
    if (school.email) {
      const eLines = doc.splitTextToSize(school.email, sideW - 10);
      eLines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    sy += 3;
    doc.setDrawColor(255, 255, 255);
    doc.line(8, sy, sideW - 8, sy);
    sy += 5;
    if (school.code) { doc.setFont('helvetica', 'bold'); doc.text('AFFILIATION', sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); const cl = doc.splitTextToSize(school.code, sideW - 10); cl.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; }); sy += 2; }
    if (school.branch) { doc.setFont('helvetica', 'bold'); doc.text('BRANCH', sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(school.branch, sideW / 2, sy, { align: 'center' }); sy += 5; }
    doc.setFont('helvetica', 'bold'); doc.text('ACADEMIC YEAR', sideW / 2, sy, { align: 'center' }); sy += 3.5;
    doc.setFont('helvetica', 'normal'); doc.text(meta.academicYear || '—', sideW / 2, sy, { align: 'center' });

    const left = sideW + 10;
    const right = w - 14;
    let y = 18;
    doc.setTextColor(...color);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    const titleSplit = doc.splitTextToSize(headerTitle, right - left);
    titleSplit.forEach((ln) => { doc.text(ln, left, y); y += 7; });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`#${meta.receiptNo}   ·   ${meta.date}`, left, y);
    y += 8;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Student', left, y);
    doc.text('Payment', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(student.name || '—', left, y);
    doc.text(`${meta.paymentMode}${meta.txnRef ? '  ·  ' + meta.txnRef : ''}`, (left + right) / 2, y);
    y += 5;
    doc.text(`Adm: ${student.admissionNo || '—'}`, left, y);
    doc.text(`Term: ${meta.term || '—'}`, (left + right) / 2, y);
    y += 5;
    const cls = [student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  |  ');
    if (cls) doc.text(cls, left, y);
    doc.text(`Cashier: ${meta.cashier || '—'}`, (left + right) / 2, y);
    y += 5;
    if (student.fatherName) { doc.text(`Father: ${student.fatherName}`, left, y); y += 5; }
    if (student.phone || student.email) {
      doc.text([student.phone, student.email].filter(Boolean).join('  ·  '), left, y);
      y += 5;
    }

    y += 4;
    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);

    return doc;
  };

  // 3. MINIMAL
  const renderMinimal = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = [40, 40, 40];

    let y = 18;
    if (logo) drawPdfLogo(doc, logo, left, 12, 18, school.name, [240, 240, 240]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(school.name || 'School', logo ? left + 22 : left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const headLine = [school.address, school.phone, school.email].filter(Boolean).join('  ·  ');
    if (headLine) {
      const lines = doc.splitTextToSize(headLine, right - (logo ? left + 22 : left));
      lines.forEach((ln) => { doc.text(ln, logo ? left + 22 : left, y); y += 4; });
    }
    if (school.code) { doc.text(`${school.code}${school.branch ? '  ·  ' + school.branch : ''}`, logo ? left + 22 : left, y); y += 4; }

    y += 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.6);
    doc.line(left, y, right, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(headerTitle, left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(meta.receiptNo, right, y, { align: 'right' });
    y += 6;
    doc.setFontSize(9);
    doc.text(`${meta.date}  ·  ${meta.academicYear}  ·  ${meta.term}`, left, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Student:', left, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`${student.name || '—'}${student.admissionNo ? `  (${student.admissionNo})` : ''}`, left + 20, y);
    y += 5;
    const cls = [student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`, student.fatherName && `S/o D/o ${student.fatherName}`].filter(Boolean).join('  ·  ');
    if (cls) { doc.text(cls, left, y); y += 5; }
    if (student.phone || student.email) { doc.text([student.phone, student.email].filter(Boolean).join('  ·  '), left, y); y += 5; }
    y += 2;

    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);
    return doc;
  };

  // 4. BANNER
  const renderBanner = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, w, 42, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 6, 30, school.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(school.name || 'School', logo ? left + 34 : left, 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(headerTitle, logo ? left + 34 : left, 26);
    doc.setFontSize(9);
    const sub = [school.address, school.phone].filter(Boolean).join('  ·  ');
    if (sub) {
      const lines = doc.splitTextToSize(sub, w - (logo ? left + 34 : left) - 42);
      lines.forEach((ln, i) => doc.text(ln, logo ? left + 34 : left, 32 + i * 4));
    }
    doc.text(`#${meta.receiptNo}`, right, 14, { align: 'right' });
    doc.text(meta.date, right, 20, { align: 'right' });
    doc.text(meta.academicYear, right, 26, { align: 'right' });
    if (school.code) doc.text(school.code, right, 32, { align: 'right' });

    let y = 52;
    doc.setTextColor(...color);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Student', left, y);
    doc.text('Payment', (left + right) / 2, y);
    y += 5;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(student.name || '—', left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${meta.paymentMode}${meta.txnRef ? '  ·  Ref: ' + meta.txnRef : ''}`, (left + right) / 2, y);
    y += 5;
    doc.setFontSize(9);
    const cls = [student.admissionNo && `Adm: ${student.admissionNo}`, student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  |  ');
    if (cls) doc.text(cls, left, y);
    doc.text(`Term: ${meta.term || '—'}`, (left + right) / 2, y);
    y += 5;
    if (student.fatherName) { doc.text(`Father: ${student.fatherName}`, left, y); }
    doc.text(`Cashier: ${meta.cashier || '—'}`, (left + right) / 2, y);
    y += 5;
    if (student.phone || student.email) { doc.text([student.phone, student.email].filter(Boolean).join('  ·  '), left, y); y += 5; }

    y += 3;
    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);
    return doc;
  };

  // 5. THERMAL 80mm
  const renderThermal = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 8;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 16) / 2, 4, 16, school.name, [240, 240, 240]);
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text(school.name || 'School', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    if (school.address) {
      const lines = doc.splitTextToSize(school.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3; });
    }
    if (school.phone) { doc.text(school.phone, w / 2, y, { align: 'center' }); y += 3; }
    if (school.code) { doc.text(school.code, w / 2, y, { align: 'center' }); y += 3; }
    y += 1;
    doc.setDrawColor(0, 0, 0);
    doc.line(5, y, w - 5, y);
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(headerTitle, w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`#${meta.receiptNo}`, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    doc.text(`AY: ${meta.academicYear}  ${meta.term ? ' · ' + meta.term : ''}`, 5, y); y += 4;
    doc.text(`Student: ${student.name || '-'}`, 5, y); y += 4;
    if (student.admissionNo) { doc.text(`Adm: ${student.admissionNo}`, 5, y); y += 4; }
    const cls = [student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  ');
    if (cls) { doc.text(cls, 5, y); y += 4; }
    if (meta.cashier) { doc.text(`By: ${meta.cashier}`, 5, y); y += 4; }
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Particulars', 5, y);
    doc.text('Amount', w - 5, y, { align: 'right' });
    y += 2;
    doc.line(5, y, w - 5, y);
    y += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    items.forEach((it) => {
      const lines = doc.splitTextToSize(it.head || '-', 45);
      doc.text(lines, 5, y);
      doc.text(formatINR(it.amount), w - 5, y, { align: 'right' });
      y += Math.max(4, lines.length * 3.5);
    });
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 8);
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += bold ? 6 : 4;
    };
    row('Subtotal', formatINR(totals.itemsSum));
    if (totals.prev > 0) row('Prev. Bal.', formatINR(totals.prev));
    if (totals.fine > 0) row('Late Fee', formatINR(totals.fine));
    if (totals.disc > 0) row('Discount', `- ${formatINR(totals.disc)}`);
    if (totals.sch > 0) row('Scholarship', `- ${formatINR(totals.sch)}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;
    doc.setTextColor(...color);
    row('TOTAL', `Rs. ${formatINR(finalAmount)}`, true);
    doc.setTextColor(0, 0, 0);
    row('Paid', `Rs. ${formatINR(totals.paid)}`);
    if (totals.balanceDue > 0) {
      doc.setTextColor(185, 28, 28);
      row('Balance', `Rs. ${formatINR(totals.balanceDue)}`, true);
      doc.setTextColor(0, 0, 0);
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    const wl = doc.splitTextToSize(`Rupees ${amountInWords} Only`, w - 10);
    wl.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3; });
    y += 2;
    doc.setFont('helvetica', 'normal');
    doc.text(`Mode: ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Thank You!', w / 2, y, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    modern: renderModern,
    minimal: renderMinimal,
    banner: renderBanner,
    thermal: renderThermal,
  };

  const buildPDF = () => (TEMPLATE_RENDERERS[template] || renderClassic)();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`School_${typeLabel.replace(/\s+/g, '_')}_${meta.receiptNo || 'receipt'}.pdf`);
  };
  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  // ---------- PREVIEW SHARED PIECES ----------
  const LogoImg = ({ logo: l, style }) =>
    l ? <img src={l} alt="logo" style={{ objectFit: 'contain', ...style }} /> : null;

  const ItemsTable = ({ compact = false }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 10 : 12 }}>
      <thead>
        <tr style={{ background: accent, color: '#fff' }}>
          <th style={{ padding: 6, textAlign: 'left', width: 24 }}>#</th>
          <th style={{ padding: 6, textAlign: 'left' }}>Fee Head / Particulars</th>
          <th style={{ padding: 6, textAlign: 'right' }}>Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, idx) => (
          <tr key={it.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
            <td style={{ padding: 6 }}>{idx + 1}</td>
            <td style={{ padding: 6 }}>{it.head || '—'}</td>
            <td style={{ padding: 6, textAlign: 'right' }}>{formatINR(it.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const TotalsBlock = () => (
    <div style={{ marginTop: 12, marginLeft: 'auto', width: '60%', fontSize: 12 }}>
      <Row label="Subtotal" value={`₹ ${formatINR(totals.itemsSum)}`} />
      {totals.prev > 0 && <Row label="Previous Balance" value={`₹ ${formatINR(totals.prev)}`} />}
      {totals.fine > 0 && <Row label="Late Fee / Fine" value={`₹ ${formatINR(totals.fine)}`} />}
      {totals.disc > 0 && <Row label="Discount" value={`- ₹ ${formatINR(totals.disc)}`} />}
      {totals.sch > 0 && <Row label="Scholarship / Concession" value={`- ₹ ${formatINR(totals.sch)}`} />}
      {charges.roundOff && Math.abs(totals.roundOff) > 0.001 && (
        <Row label="Round Off" value={`${totals.roundOff >= 0 ? '+' : '-'} ₹ ${formatINR(Math.abs(totals.roundOff))}`} />
      )}
      <div style={{ borderTop: `2px solid ${accent}`, marginTop: 6, paddingTop: 6 }}>
        <Row label="TOTAL PAYABLE" value={`₹ ${formatINR(finalAmount)}`} bold big />
        <Row label="Amount Paid" value={`₹ ${formatINR(totals.paid)}`} bold />
        {totals.balanceDue > 0 && <Row label="Balance Due" value={`₹ ${formatINR(totals.balanceDue)}`} bold color="#b91c1c" />}
      </div>
      <div style={{ marginTop: 6, fontSize: 11, fontStyle: 'italic', color: '#475569' }}>
        Amount in words: Rupees {amountInWords} Only
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: '#334155' }}>
        {meta.paymentMode === 'Cheque' || meta.paymentMode === 'DD'
          ? `${meta.paymentMode} No: ${meta.chequeNo || '—'}${meta.bankName ? '  ·  ' + meta.bankName : ''}`
          : `Mode: ${meta.paymentMode}${meta.txnRef ? '  ·  Ref: ' + meta.txnRef : ''}`}
      </div>
    </div>
  );

  const Row = ({ label, value, bold, big, color }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '3px 0',
      fontWeight: bold ? 700 : 400,
      fontSize: big ? 14 : 12,
      color: color || (bold ? accent : '#0f172a'),
    }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  const NotesBlock = () =>
    notes ? (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 700, color: accent, marginBottom: 4 }}>Notes / Terms</div>
        <div style={{ fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap' }}>{notes}</div>
      </div>
    ) : null;

  const previewBase = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
    color: '#0f172a',
  };

  const renderPreview = () => {
    if (template === 'classic') {
      return (
        <div style={{ ...previewBase, padding: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {logo && <LogoImg logo={logo} style={{ height: 56, width: 56 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: accent }}>{school.name || 'School'}</div>
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginTop: 2 }}>
                {school.address}
                {(school.phone || school.email) && <><br />{[school.phone, school.email].filter(Boolean).join(' · ')}</>}
                {(school.code || school.branch) && <><br />{[school.code && `Affiliation: ${school.code}`, school.branch].filter(Boolean).join('  ·  ')}</>}
              </div>
            </div>
          </div>
          <div style={{ borderTop: `2px solid ${accent}`, margin: '12px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: accent }}>{headerTitle}</div>
            <div style={{ fontSize: 11, color: '#475569', textAlign: 'right' }}>
              Receipt No: <strong>{meta.receiptNo}</strong><br />
              {meta.date}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
            <div>
              <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Student</div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{student.name || '—'}</div>
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
                {student.admissionNo && <>Adm. No: {student.admissionNo}<br /></>}
                {[student.classGrade, student.section && `Sec. ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  ·  ')}
                {student.fatherName && <><br />Father: {student.fatherName}</>}
                {student.motherName && <><br />Mother: {student.motherName}</>}
                {(student.phone || student.email) && <><br />{[student.phone, student.email].filter(Boolean).join('  ·  ')}</>}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Session Info</div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                Academic Year: <strong>{meta.academicYear || '—'}</strong><br />
                Term: {meta.term || '—'}<br />
                Cashier: {meta.cashier || '—'}<br />
                Payment Mode: {meta.paymentMode}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14 }}><ItemsTable /></div>
          <TotalsBlock />
          <NotesBlock />
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 180, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
              Parent / Student Signature
            </div>
            <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
              Authorised Signatory
            </div>
          </div>
        </div>
      );
    }

    if (template === 'modern') {
      return (
        <div style={{ ...previewBase, display: 'flex', minHeight: 520 }}>
          <div style={{ background: accent, color: '#fff', padding: 16, width: 180, flexShrink: 0 }}>
            {logo && (
              <div style={{ background: '#fff', borderRadius: 6, padding: 4, textAlign: 'center', marginBottom: 10 }}>
                <LogoImg logo={logo} style={{ height: 56 }} />
              </div>
            )}
            <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{school.name || 'School'}</div>
            <div style={{ fontSize: 10, opacity: 0.9, marginTop: 6, lineHeight: 1.5 }}>
              {school.address}
              {school.phone && <><br />{school.phone}</>}
              {school.email && <><br />{school.email}</>}
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.4)', margin: '10px 0' }} />
            {school.code && (
              <div style={{ fontSize: 10 }}>
                <div style={{ opacity: 0.85, fontWeight: 700 }}>AFFILIATION</div>
                <div>{school.code}</div>
              </div>
            )}
            {school.branch && (
              <div style={{ fontSize: 10, marginTop: 6 }}>
                <div style={{ opacity: 0.85, fontWeight: 700 }}>BRANCH</div>
                <div>{school.branch}</div>
              </div>
            )}
            <div style={{ fontSize: 10, marginTop: 6 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>ACADEMIC YEAR</div>
              <div>{meta.academicYear}</div>
            </div>
          </div>
          <div style={{ flex: 1, padding: 20 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>{headerTitle}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>#{meta.receiptNo}  ·  {meta.date}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginTop: 14 }}>
              <div>
                <div style={{ fontWeight: 700 }}>Student</div>
                <div style={{ fontWeight: 700 }}>{student.name || '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  {[student.admissionNo && `Adm: ${student.admissionNo}`, student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  |  ')}
                </div>
                {student.fatherName && <div style={{ fontSize: 11, color: '#64748b' }}>Father: {student.fatherName}</div>}
                {(student.phone || student.email) && <div style={{ fontSize: 11, color: '#64748b' }}>{[student.phone, student.email].filter(Boolean).join('  ·  ')}</div>}
              </div>
              <div>
                <div><strong>Term:</strong> {meta.term || '—'}</div>
                <div><strong>Cashier:</strong> {meta.cashier || '—'}</div>
                <div><strong>Payment:</strong> {meta.paymentMode}{meta.txnRef ? ` (${meta.txnRef})` : ''}</div>
              </div>
            </div>
            <div style={{ marginTop: 14 }}><ItemsTable /></div>
            <TotalsBlock />
            <NotesBlock />
          </div>
        </div>
      );
    }

    if (template === 'minimal') {
      return (
        <div style={{ ...previewBase, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {logo && <LogoImg logo={logo} style={{ height: 44, width: 44 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{school.name || 'School'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {[school.address, school.phone, school.email].filter(Boolean).join('  ·  ')}
                {school.code && <> · {school.code}</>}
              </div>
            </div>
          </div>
          <div style={{ borderTop: '2px solid #0f172a', margin: '12px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 1 }}>{headerTitle}</div>
            <div style={{ fontSize: 12 }}>{meta.receiptNo}</div>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
            {meta.date}  ·  {meta.academicYear}  ·  {meta.term}
          </div>
          <div style={{ marginTop: 12, fontSize: 12 }}>
            <strong>Student:</strong> {student.name || '—'}
            {student.admissionNo && <> ({student.admissionNo})</>}
            <br />
            <span style={{ color: '#64748b' }}>
              {[student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`, student.fatherName && `S/o D/o ${student.fatherName}`].filter(Boolean).join('  ·  ')}
            </span>
          </div>
          <div style={{ marginTop: 12 }}><ItemsTable /></div>
          <TotalsBlock />
          <NotesBlock />
        </div>
      );
    }

    if (template === 'banner') {
      return (
        <div style={{ ...previewBase }}>
          <div style={{ background: accent, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            {logo && (
              <div style={{ background: '#fff', borderRadius: 6, padding: 4 }}>
                <LogoImg logo={logo} style={{ height: 56, width: 56 }} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 20 }}>{school.name || 'School'}</div>
              <div style={{ fontSize: 11, opacity: 0.95 }}>{headerTitle}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                {[school.address, school.phone].filter(Boolean).join('  ·  ')}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11 }}>
              <div>#{meta.receiptNo}</div>
              <div>{meta.date}</div>
              <div>{meta.academicYear}</div>
              {school.code && <div>{school.code}</div>}
            </div>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
            <div>
              <div style={{ fontWeight: 700, color: accent }}>Student</div>
              <div style={{ fontWeight: 700 }}>{student.name || '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {[student.admissionNo && `Adm: ${student.admissionNo}`, student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  |  ')}
              </div>
              {student.fatherName && <div style={{ fontSize: 11, color: '#64748b' }}>Father: {student.fatherName}</div>}
            </div>
            <div style={{ background: '#fef3c7', padding: 8, borderRadius: 6 }}>
              <div style={{ fontWeight: 700, color: accent, fontSize: 11 }}>Payment</div>
              <div>Term: <strong>{meta.term || '—'}</strong></div>
              <div>Cashier: <strong>{meta.cashier || '—'}</strong></div>
              <div>Mode: <strong>{meta.paymentMode}</strong>{meta.txnRef && <> · {meta.txnRef}</>}</div>
            </div>
          </div>
          <div style={{ padding: '0 16px' }}><ItemsTable /></div>
          <div style={{ padding: '0 16px 16px' }}>
            <TotalsBlock />
            <NotesBlock />
          </div>
        </div>
      );
    }

    // Thermal
    return (
      <div style={{ ...previewBase, padding: 14, maxWidth: 320, margin: '12px auto', border: '1px dashed #cbd5e1' }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <LogoImg logo={logo} style={{ height: 46 }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, color: accent }}>
          {school.name || 'School'}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>
          {school.address}
          {school.phone && <><br />{school.phone}</>}
          {school.code && <><br />{school.code}</>}
        </div>
        <div style={{ borderTop: '1px dashed #94a3b8', margin: '10px 0' }} />
        <div style={{ textAlign: 'center', fontWeight: 700 }}>{headerTitle}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 6 }}>
          <span>#{meta.receiptNo}</span>
          <span>{meta.date}</span>
        </div>
        <div style={{ fontSize: 11, marginTop: 2 }}>
          AY: <strong>{meta.academicYear}</strong>  {meta.term && <>· {meta.term}</>}
        </div>
        <div style={{ fontSize: 11 }}>
          Student: <strong>{student.name || '—'}</strong>
          {student.admissionNo && <> ({student.admissionNo})</>}
        </div>
        <div style={{ fontSize: 11 }}>
          {[student.classGrade, student.section && `Sec ${student.section}`, student.rollNo && `Roll ${student.rollNo}`].filter(Boolean).join('  ')}
        </div>
        {meta.cashier && <div style={{ fontSize: 11 }}>By: {meta.cashier}</div>}
        <div style={{ marginTop: 8 }}><ItemsTable compact /></div>
        <TotalsBlock />
        <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: '#64748b' }}>
          Paid via <strong>{meta.paymentMode}</strong>
        </div>
        <div style={{ marginTop: 4, textAlign: 'center', fontWeight: 700, color: accent }}>
          Thank You!
        </div>
      </div>
    );
  };

  const isCheque = meta.paymentMode === 'Cheque' || meta.paymentMode === 'DD';

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="School Fees Receipt Generator (Term, Exam, Transport, Annual & Fine)"
        description="Free School Fees Receipt Generator - create term fee, exam fee, transport/hostel, annual/admission and late-fee receipts with student details, fee head breakdown, scholarship/discount, balance due and 5 printable templates. Download as PDF."
        keywords="school fees receipt generator, tuition fee receipt, exam fee receipt, hostel fee receipt, transport fee receipt, admission fee receipt, school fee bill format, fee slip generator, CBSE school receipt, fee challan generator india"
        canonicalPath="/school-fees-receipt"
        calculatorType="SchoolFeesReceiptGenerator"
        calculatorDescription="Generate school fee receipts for tuition, exam, transport, hostel, admission and fines with student info, fee head breakdown, scholarship/discount and 5 printable templates."
      />

      <div className="calculator-header">
        <h1>School Fees Receipt Generator</h1>
        <p>Tuition, Exam, Transport, Hostel, Annual & Fine receipts — fee head breakdown with 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Receipt Type */}
          <div className="input-group">
            <label>Receipt Type</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {RECEIPT_TYPES.map((opt) => {
                const active = receiptType === opt.v;
                const Ic = opt.Icon;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    className={`btn ${active ? 'btn-primary' : 'btn-outline'}`}
                    style={{ padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => switchType(opt.v)}
                  >
                    <Ic /> {opt.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template */}
          <div className="input-group">
            <label>Receipt Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.accent, border: active ? '1px solid #fff' : 'none' }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="School Logo" accent={accent} />

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>School Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>School Name</label>
              <input type="text" value={school.name} onChange={(e) => setSchool({ ...school, name: e.target.value })} placeholder="e.g. Sunrise Public School" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input type="text" value={school.phone} onChange={(e) => setSchool({ ...school, phone: e.target.value })} placeholder="+91 ..." />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={school.address} onChange={(e) => setSchool({ ...school, address: e.target.value })} placeholder="Street, City, State, PIN" />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email</label>
              <input type="text" value={school.email} onChange={(e) => setSchool({ ...school, email: e.target.value })} placeholder="office@..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Affiliation / Board Code</label>
              <input type="text" value={school.code} onChange={(e) => setSchool({ ...school, code: e.target.value })} placeholder="e.g. CBSE-AFF-..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Branch / Campus</label>
              <input type="text" value={school.branch} onChange={(e) => setSchool({ ...school, branch: e.target.value })} placeholder="Main / Branch" />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Receipt Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Receipt No</label>
              <input type="text" value={meta.receiptNo} onChange={(e) => setMeta({ ...meta, receiptNo: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Date</label>
              <input type="text" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} placeholder="DD/MM/YYYY" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Academic Year</label>
              <input type="text" value={meta.academicYear} onChange={(e) => setMeta({ ...meta, academicYear: e.target.value })} placeholder="e.g. 2025-26" />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Term / Installment</label>
              <input type="text" value={meta.term} onChange={(e) => setMeta({ ...meta, term: e.target.value })} placeholder="e.g. Term I (Apr - Sep)" />
            </div>
            <div className="input-group" style={{ flex: 2 }}>
              <label>Cashier / Accountant</label>
              <input type="text" value={meta.cashier} onChange={(e) => setMeta({ ...meta, cashier: e.target.value })} placeholder="Mrs./Mr." />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Payment Mode</label>
              <select value={meta.paymentMode} onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {!isCheque && (
              <div className="input-group" style={{ flex: 2 }}>
                <label>Transaction / UTR / Ref No</label>
                <input type="text" value={meta.txnRef} onChange={(e) => setMeta({ ...meta, txnRef: e.target.value })} placeholder="e.g. UPI/QR-...., UTR..." />
              </div>
            )}
            {isCheque && (
              <>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>{meta.paymentMode} No</label>
                  <input type="text" value={meta.chequeNo} onChange={(e) => setMeta({ ...meta, chequeNo: e.target.value })} placeholder="e.g. 123456" />
                </div>
                <div className="input-group" style={{ flex: 2 }}>
                  <label>Bank Name</label>
                  <input type="text" value={meta.bankName} onChange={(e) => setMeta({ ...meta, bankName: e.target.value })} placeholder="e.g. SBI, HDFC" />
                </div>
              </>
            )}
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Student Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Student Name</label>
              <input type="text" value={student.name} onChange={(e) => setStudent({ ...student, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Admission No</label>
              <input type="text" value={student.admissionNo} onChange={(e) => setStudent({ ...student, admissionNo: e.target.value })} placeholder="ADM-..." />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Class / Grade</label>
              <input type="text" value={student.classGrade} onChange={(e) => setStudent({ ...student, classGrade: e.target.value })} placeholder="e.g. Class VIII" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Section</label>
              <input type="text" value={student.section} onChange={(e) => setStudent({ ...student, section: e.target.value })} placeholder="e.g. B" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Roll No</label>
              <input type="text" value={student.rollNo} onChange={(e) => setStudent({ ...student, rollNo: e.target.value })} placeholder="e.g. 17" />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Father's Name</label>
              <input type="text" value={student.fatherName} onChange={(e) => setStudent({ ...student, fatherName: e.target.value })} placeholder="Mr. ..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Mother's Name</label>
              <input type="text" value={student.motherName} onChange={(e) => setStudent({ ...student, motherName: e.target.value })} placeholder="Mrs. ..." />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Contact Phone</label>
              <input type="text" value={student.phone} onChange={(e) => setStudent({ ...student, phone: e.target.value })} placeholder="+91 ..." />
            </div>
            <div className="input-group" style={{ flex: 2 }}>
              <label>Contact Email</label>
              <input type="text" value={student.email} onChange={(e) => setStudent({ ...student, email: e.target.value })} placeholder="parent@..." />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 8 }}>Fee Heads</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it, idx) => (
              <div key={it.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#f8fafc' }}>
                <div className="flex-row" style={{ alignItems: 'flex-end' }}>
                  <div className="input-group" style={{ flex: 3, marginBottom: 0 }}>
                    <label>{idx + 1}. Fee Head / Particulars</label>
                    <input type="text" value={it.head} onChange={(e) => updateItem(it.id, { head: e.target.value })} placeholder="e.g. Tuition Fee" />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Amount ₹</label>
                    <input type="number" min="0" step="0.01" value={it.amount} onChange={(e) => updateItem(it.id, { amount: e.target.value })} />
                  </div>
                  <button type="button" onClick={() => removeItem(it.id)} className="btn btn-outline" style={{ padding: '8px 10px', marginBottom: 0 }} title="Remove">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addItem} className="btn btn-outline" style={{ marginTop: 10 }}>
            <FaPlus /> Add Fee Head
          </button>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Adjustments</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Previous Balance ₹</label>
              <input type="number" min="0" step="0.01" value={charges.previousBalance} onChange={(e) => setCharges({ ...charges, previousBalance: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Late Fee / Fine ₹</label>
              <input type="number" min="0" step="0.01" value={charges.lateFine} onChange={(e) => setCharges({ ...charges, lateFine: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Discount ₹</label>
              <input type="number" min="0" step="0.01" value={charges.discount} onChange={(e) => setCharges({ ...charges, discount: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Scholarship / Concession ₹</label>
              <input type="number" min="0" step="0.01" value={charges.scholarship} onChange={(e) => setCharges({ ...charges, scholarship: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Amount Paid ₹ <span style={{ fontSize: 11, color: '#64748b' }}>(0 = full payment)</span></label>
              <input type="number" min="0" step="0.01" value={charges.amountPaid} onChange={(e) => setCharges({ ...charges, amountPaid: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={charges.roundOff} onChange={(e) => setCharges({ ...charges, roundOff: e.target.checked })} />
                Round off total
              </label>
            </div>
          </div>

          <div className="input-group" style={{ marginTop: 12 }}>
            <label>Notes / Terms</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Fees once paid are non-refundable..."
              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontFamily: 'inherit', fontSize: 13 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button type="button" onClick={handleDownloadPDF} className="btn btn-primary">
              <FaDownload /> Download PDF
            </button>
            <button type="button" onClick={handlePrint} className="btn btn-outline">
              <FaPrint /> Print
            </button>
            <button type="button" onClick={handleReset} className="btn btn-outline">
              <FaRedo /> Reset
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <h2 style={{ marginBottom: 10 }}>Live Preview</h2>
          {renderPreview()}

          <div style={{ marginTop: 18, fontSize: 12, color: '#64748b' }}>
            <strong>Tip:</strong> Use the Quick Slip 80mm template for thermal printers at the school accounts counter, and the Classic/Modern A4 templates for sharing PDFs with parents.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolFeesReceiptGenerator;
